import styled from '@emotion/styled'
import * as React from 'react'

import { Button, BUTTON_DEFAULT_RADIUS } from '~ui/components/buttons/Button/Button'
import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { IconMinus } from '~ui/components/icons/specific/IconAmountControl'
import { Box, GridCentered } from '~ui/components/layout/Box'
import { Number } from '~ui/components/typography/Number/Number'
import { Text } from '~ui/components/typography/Text/Text'
import { cn } from '~ui/libs/classnames'
import { TSingleSpace } from '~ui/styleGuide/spaces'

type TProps = RequiredChildren & {
  /**
   * current amount
   */
  value: number
  /**
   * once input hits this value, the increase button will become disabled
   */
  maxValue?: number
  /**
   * disables both increase and decrease button
   */
  isDisabled?: boolean
  /**
   * indicator whether it is possible to add more addons
   */
  canAdd?: boolean
  /**
   * callback called whenever user changes the input (+1 or -1)
   */
  onChange: (newValue: number) => void
  /**
   * minWidth of the button with label
   */
  minWidth?: PropsOf<typeof Button>['minWidth']
}

const StyledAddAmountButton = styled(Box)`
  display: flex;
  /* justify the increase button to the right side */
  justify-items: right;

  .decrease-value {
    /* by default, the decrease btn & the value are hidden behind the increase btn */
    max-width: 0;
    transform: translateX(100%);
    transition: transform 0.2s, max-width 0.2s;
  }

  .increase-btn {
    margin-left: auto;
  }

  &.expanded {
    .decrease-value {
      max-width: 5rem;
      /* once the input value is > 0 and the input is expanded, move the decrease btn & the value counter to its original position */
      transform: translateX(0%);
    }
  }
`

const buttonRadius = BUTTON_DEFAULT_RADIUS
const buttonPadding: TSingleSpace = `smallMedium`

// TODO maybe add onLongClick to a Button & use it here to add multiple items at once?
// TODO make decrease-btn not focusable when in expanded
// TODO Button on onKeyPress={onClick} (keyboard accessible)
export const AddAmountButton: React.FC<TProps> = ({
  value,
  maxValue,
  // default min-width of the increase button to hide the counter value during the expansion transition
  minWidth = `5rem`,
  onChange,
  children,
  canAdd = true,
  isDisabled = false,
}) => {
  // do not go bellow 0
  const correctedValue = Math.max(value, 0)
  const isExpanded = correctedValue > 0
  const valueOverflownMaxLimit = Boolean(maxValue && correctedValue >= maxValue)
  const isAddButtonDisabled = isDisabled || !canAdd || valueOverflownMaxLimit

  return (
    <StyledAddAmountButton className={cn({ expanded: isExpanded })}>
      <GridCentered gridTemplateColumns={`auto 1fr`} className={`decrease-value`}>
        <ButtonIcon
          height={`100%`}
          icon={IconMinus}
          iconSize={`0.9rem`}
          iconColor={`specialColors.error`}
          padding={buttonPadding}
          bgColor={`inputOnPrimary`}
          shadow={isExpanded ? `light` : `none`}
          state={isDisabled ? `disabled` : `enabled`}
          onClick={() => onChange(Math.max(correctedValue - 1, 0))}
          radius={{
            topRight: `none`,
            bottomRight: `none`,
            topLeft: buttonRadius,
            bottomLeft: buttonRadius,
          }}
        />
        <Box minWidth={`2.5rem`} opacity={correctedValue > 0 ? 1 : 0}>
          <Text textAlign={`center`}>
            <Number>{correctedValue}</Number>&times;
          </Text>
        </Box>
      </GridCentered>
      <Button
        shadow={`light`}
        padding={buttonPadding}
        minWidth={minWidth}
        className={`increase-btn`}
        onClick={() => onChange(correctedValue + 1)}
        state={isAddButtonDisabled ? `disabled` : `enabled`}
        bgColor={correctedValue === 0 ? `inputOnPrimary` : `success`}
        radius={{
          topRight: buttonRadius,
          bottomRight: buttonRadius,
          topLeft: isExpanded ? `none` : buttonRadius,
          bottomLeft: isExpanded ? `none` : buttonRadius,
        }}
      >
        {children}
      </Button>
    </StyledAddAmountButton>
  )
}
